<script>
import { orderBy } from 'lodash'
import baseMixin from '@/mixins/baseMixin'
import { TimesheetPrimaryGroupingType } from '@/shared/constants/timesheet/TimesheetPrimaryGroupingType'
import TimesheetExpansionPanelHeaderStatistic from './timesheet-expansion-panel-header-statistic.vue'
import TimesheetExpansionPanelContent from './timesheet-expansion-panel-content.vue'

export default {
  name: 'TimesheetPrimaryGroupPanel',
  components: {
    TimesheetExpansionPanelHeaderStatistic,
    TimesheetExpansionPanelContent,
  },
  mixins: [baseMixin],
  props: {
    primaryGrouping: {
      type: String,
      default: TimesheetPrimaryGroupingType.client,
    },
    clientDisplayables: {
      type: Array,
      required: true,
    },
    candidateDisplayables: {
      type: Array,
      required: true,
    },
    loadTimesheetHandler: {
      type: Function,
      default: function() {
        return null
      },
    },
  },
  data: () => {
    return {
      clientAccordion: [],
      candidateAccordion: [],
      TimesheetPrimaryGroupingType,
    }
  },
  computed: {
    isPrimaryGroupingByClient() {
      return this.primaryGrouping === TimesheetPrimaryGroupingType.client
    },
    clientDisplayablesWithPendingApprovals() {
      return orderBy(
        this.clientDisplayables.filter(
          (group) => group.pendingApprovalCount > 0
        ),
        (item) => item.name
      )
    },

    candidateDisplayablesWithPendingApprovals() {
      return orderBy(
        this.candidateDisplayables.filter(
          (group) => group.pendingApprovalCount > 0
        ),
        (item) => item.name
      )
    },
  },
  watch: {
    primaryGrouping(value) {
      if (value === TimesheetPrimaryGroupingType.client) {
        this.candidateAccordion = []
      } else {
        this.clientAccordion = []
      }
    },
  },
  methods: {
    timesheetsUpdated(selectedTimesheets) {
      this.$emit('timesheetsUpdated', selectedTimesheets)
    },
    changeTime(timesheetData) {
      this.$emit('changeTime', timesheetData)
    },
    cancelBookingSubmitted(payload) {
      this.$emit('cancelBookingSubmitted', payload)
    },
  },
}
</script>

<template>
  <div>
    <v-expansion-panels
      v-if="isPrimaryGroupingByClient"
      v-model="clientAccordion"
      data-testid="timesheet-expansion-panels-client"
      accordion
      flat
      multiple
      hover
    >
      <v-expansion-panel
        v-for="clientGroup of clientDisplayablesWithPendingApprovals"
        :key="clientGroup.id"
        class="transparent"
      >
        <v-expansion-panel-header>
          <v-container class="pa-0">
            <v-row class="ma-0">
              <v-col class="pa-0" cols="9">
                {{ clientGroup.name }}
              </v-col>
              <v-col class="pa-0" cols="3">
                <TimesheetExpansionPanelHeaderStatistic
                  :pending-approval-count="clientGroup.pendingApprovalCount"
                  :approved-count="clientGroup.approvedCount"
                  :pending-review-count="clientGroup.pendingReviewCount"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <TimesheetExpansionPanelContent
            :is-data-loaded="clientGroup.isLoaded"
            :primary-group-type="TimesheetPrimaryGroupingType.client"
            :primary-group-key-id="clientGroup.id"
            :pending-approval-displayable="
              clientGroup.pendingApprovalDisplayable
            "
            :load-timesheet-handler="loadTimesheetHandler"
            @timesheetsUpdated="timesheetsUpdated"
            @changeTime="changeTime"
            @cancelBookingSubmitted="cancelBookingSubmitted"
          />
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
    <v-expansion-panels
      v-else
      v-model="candidateAccordion"
      data-testid="timesheet-expansion-panels-candidate"
      accordion
      flat
      multiple
      hover
    >
      <v-expansion-panel
        v-for="candidateGroup of candidateDisplayablesWithPendingApprovals"
        :key="candidateGroup.id"
        class="transparent"
      >
        <v-expansion-panel-header>
          <v-container class="pa-0">
            <v-row class="ma-0">
              <v-col class="pa-0" cols="9">
                {{ candidateGroup.name }}
              </v-col>
              <v-col class="pa-0" cols="3">
                <TimesheetExpansionPanelHeaderStatistic
                  :pending-approval-count="candidateGroup.pendingApprovalCount"
                  :approved-count="candidateGroup.approvedCount"
                  :pending-review-count="candidateGroup.pendingReviewCount"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <TimesheetExpansionPanelContent
            :is-data-loaded="candidateGroup.isLoaded"
            :primary-group-type="TimesheetPrimaryGroupingType.candidate"
            :primary-group-key-id="candidateGroup.id"
            :pending-approval-displayable="
              candidateGroup.pendingApprovalDisplayable
            "
            :load-timesheet-handler="loadTimesheetHandler"
            @timesheetsUpdated="timesheetsUpdated"
            @changeTime="changeTime"
            @cancelBookingSubmitted="cancelBookingSubmitted"
          />
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>
